/**
 * ScrollSprungOverlay — sichtbares Debug-HUD für den Footer-Rücksprung.
 *
 * Aktiv nur bei `?scrolldebug=1` im URL. Beobachtet den Scroll-Container
 * (.scrolly-scroll-container) per rAF und protokolliert RÜCKWÄRTS-Sprünge
 * (> 150 px in einem Tick). Pro Sprung wird festgehalten, ob gleichzeitig
 * die Gesamthöhe (scrollHeight) geschrumpft ist — das trennt die zwei
 * Hypothesen: Layout-Kollaps (Höhe schrumpft → Browser klemmt scrollTop)
 * vs. aktiver scrollTop-Schreiber (Höhe konstant).
 *
 * Erstellt 26.08.2026 für Jans iPhone-Befund: „bis zum Footer gescrollt —
 * springt plötzlich zum Slide davor zurück" (nicht in Chromium-Emulation
 * reproduzierbar, keine DevTools auf dem Gerät).
 */

import { useEffect, useState } from "react";

interface Sprung {
  zeit: string;
  vonTop: number;
  nachTop: number;
  hoeheVor: number;
  hoeheNach: number;
  /** letzter programmatischer Schreiber vor dem Sprung (leer = keiner) */
  schreiber: string;
}

export function ScrollSprungOverlay() {
  const [aktiv, setAktiv] = useState(false);
  const [spruenge, setSpruenge] = useState<Sprung[]>([]);
  const [live, setLive] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!new URLSearchParams(window.location.search).has("scrolldebug")) return;
    setAktiv(true);

    let raf = 0;
    let letzterTop = -1;
    let letzteHoehe = -1;
    // Schreiber-Falle: scrollTop-Setter am Container-INSTANZOBJEKT
    // ueberschatten (eigene Property vor dem Prototyp-Accessor) und jeden
    // programmatischen Schreiber samt Stack festhalten. Native Touch-Scrolls
    // laufen NICHT ueber den Setter — taucht beim Sprung ein Stack auf, ist
    // es JS; bleibt die Falle leer, scrollt der Browser selbst.
    let letzterSchreiber = "";
    let gefangen: HTMLElement | null = null;
    // Fokus-Falle: iOS scrollt fokussierte Elemente automatisch ins Bild
    // (laeuft an scrollTop/scrollTo VORBEI). Ein Fokuswechsel unmittelbar
    // vor dem Sprung ist dann der Taeter (z. B. CTA-Knopf beim Mounten).
    let letzterFokus = "";
    const aufFokus = (e: FocusEvent) => {
        const ziel = e.target as HTMLElement | null;
        if (!ziel) return;
        letzterFokus = `${new Date().toISOString().slice(11, 23)} Fokus auf <${ziel.tagName.toLowerCase()}> "${(
            ziel.getAttribute("aria-label") ||
            ziel.textContent ||
            ""
        )
            .trim()
            .slice(0, 40)}"`;
    };
    document.addEventListener("focusin", aufFokus, true);
    // Touch-Phase + Scroll-Ereignis-Ring: war der Finger beim Sprung unten?
    // Und passiert der Sprung atomar (ein Event) oder animiert (mehrere)?
    let letzterTouchStart = 0;
    let letzterTouchEnde = 0;
    const aufTouchStart = () => {
        letzterTouchStart = performance.now();
    };
    const aufTouchEnde = () => {
        letzterTouchEnde = performance.now();
    };
    window.addEventListener("touchstart", aufTouchStart, { passive: true, capture: true });
    window.addEventListener("touchend", aufTouchEnde, { passive: true, capture: true });
    // h pro Scroll-EVENT mitschreiben (nicht nur pro rAF-Tick): ein
    // Layout-Zucken der Inhaltshoehe MITTEN im Frame (Safari klemmt
    // scrollTop dann aufs kurzzeitig kleinere Maximum) waere im
    // Tick-Vergleich unsichtbar (Hoehe X→X), im Event-Ring nicht.
    const scrollRing: Array<{ t: number; top: number; h: number }> = [];
    const aufScroll = () => {
        const el = document.querySelector(".scrolly-scroll-container") as HTMLElement | null;
        if (!el) return;
        scrollRing.push({
            t: Math.round(performance.now()),
            top: Math.round(el.scrollTop),
            h: el.scrollHeight,
        });
        if (scrollRing.length > 14) scrollRing.shift();
    };
    document.addEventListener("scroll", aufScroll, { passive: true, capture: true });
    const stelleFalle = (el: HTMLElement) => {
        if (gefangen === el) return;
        gefangen = el;
        const proto = Object.getPrototypeOf(el);
        const nativ = Object.getOwnPropertyDescriptor(Element.prototype, "scrollTop");
        if (!nativ?.set || !nativ?.get) return;
        try {
            Object.defineProperty(el, "scrollTop", {
                configurable: true,
                get() {
                    return nativ.get!.call(el);
                },
                set(wert: number) {
                    const vorher = nativ.get!.call(el);
                    if (Math.abs(vorher - wert) > 1) {
                        const stack = String(new Error().stack || "")
                            .split("\n")
                            .slice(1, 4)
                            .map((z) => z.trim().replace(/^at /, ""))
                            .join(" ← ");
                        letzterSchreiber = `${new Date().toISOString().slice(11, 23)} setzt ${Math.round(vorher)}→${Math.round(wert)}: ${stack}`;
                    }
                    nativ.set!.call(el, wert);
                },
            });
        } catch {
            /* Falle ist reine Diagnose */
        }
        // Auch die Methoden-Wege abdecken (scrollTo/scroll/scrollBy).
        (["scrollTo", "scroll", "scrollBy"] as const).forEach((name) => {
            const orig = (el as unknown as Record<string, (...a: unknown[]) => void>)[name];
            if (typeof orig !== "function") return;
            try {
                Object.defineProperty(el, name, {
                    configurable: true,
                    value: (...args: unknown[]) => {
                        const stack = String(new Error().stack || "")
                            .split("\n")
                            .slice(1, 5)
                            .map((z) => z.trim().replace(/^at /, ""))
                            .join(" ← ");
                        letzterSchreiber = `${new Date().toISOString().slice(11, 23)} ${name}(${args
                            .map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a)))
                            .join(",")
                            .slice(0, 60)}): ${stack}`;
                        return orig.apply(el, args);
                    },
                });
            } catch {
                /* Diagnose */
            }
        });
        void proto;
    };
    // scrollIntoView/focus scrollen den Container, ohne den Setter zu
    // beruehren — global am Prototype fangen (Safari-Springer-Jagd 27.08.).
    const stackAb = (tiefe: number) =>
      String(new Error().stack || "")
        .split("\n")
        .slice(1, tiefe)
        .map((z) => z.trim().replace(/^at /, ""))
        .join(" ← ");
    const sivOrig = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function (
      this: Element,
      ...args: unknown[]
    ) {
      letzterSchreiber = `${new Date().toISOString().slice(11, 23)} scrollIntoView <${this.tagName}.${String(this.className).slice(0, 25)}>: ${stackAb(5)}`;
      return sivOrig.apply(this, args as never);
    };
    const focusOrig = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function (
      this: HTMLElement,
      ...args: unknown[]
    ) {
      letzterSchreiber = `${new Date().toISOString().slice(11, 23)} focus <${this.tagName}.${String(this.className).slice(0, 25)}>: ${stackAb(5)}`;
      return focusOrig.apply(this, args as never);
    };
    const tick = () => {
      raf = window.requestAnimationFrame(tick);
      const el = document.querySelector(
        ".scrolly-scroll-container"
      ) as HTMLElement | null;
      if (!el) return;
      stelleFalle(el);
      const top = el.scrollTop;
      const hoehe = el.scrollHeight;
      setLive(
        `top ${Math.round(top)} / max ${hoehe - el.clientHeight} (h ${hoehe})`
      );
      if (letzterTop >= 0 && letzterTop - top > 150) {
        const eintrag: Sprung = {
          zeit: new Date().toISOString().slice(11, 23),
          vonTop: Math.round(letzterTop),
          nachTop: Math.round(top),
          hoeheVor: letzteHoehe,
          hoeheNach: hoehe,
          schreiber:
            (letzterSchreiber || "(kein JS-Schreiber gefangen)") +
            (letzterFokus ? `  |  ${letzterFokus}` : "  |  kein Fokus") +
            `  |  Finger: ${
              letzterTouchStart > letzterTouchEnde
                ? "UNTEN"
                : `oben seit ${Math.round(performance.now() - letzterTouchEnde)}ms`
            }  |  Ring: ${scrollRing
              .slice(-8)
              .map((r) => `${r.top}${r.h !== hoehe ? `(h${r.h})` : ""}`)
              .join(">")}`,
        };
        letzterSchreiber = "";
        letzterFokus = "";
        setSpruenge((alt) => [...alt.slice(-2), eintrag]);
      }
      letzterTop = top;
      letzteHoehe = hoehe;
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(raf);
      Element.prototype.scrollIntoView = sivOrig;
      HTMLElement.prototype.focus = focusOrig;
      document.removeEventListener("focusin", aufFokus, true);
      window.removeEventListener("touchstart", aufTouchStart, true);
      window.removeEventListener("touchend", aufTouchEnde, true);
      document.removeEventListener("scroll", aufScroll, true);
    };
  }, []);

  if (!aktiv) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: 60,
        left: 8,
        right: 8,
        zIndex: 99999,
        background: "rgba(0,0,0,0.8)",
        color: "#0f0",
        fontFamily: "ui-monospace, monospace",
        fontSize: 11,
        lineHeight: 1.5,
        padding: "6px 8px",
        borderRadius: 6,
        pointerEvents: "none",
        whiteSpace: "pre-wrap",
      }}
    >
      {`SCROLL-DEBUG  ${live}\n`}
      {spruenge.length === 0
        ? "noch kein Rücksprung erkannt"
        : spruenge
            .map(
              (s) =>
                `${s.zeit}  SPRUNG ${s.vonTop}→${s.nachTop} (${
                  s.nachTop - s.vonTop
                }px)  Höhe ${s.hoeheVor}→${s.hoeheNach}${
                  s.hoeheNach < s.hoeheVor ? "  ⚠ HÖHE GESCHRUMPFT" : ""
                }\n  ↳ ${s.schreiber}`
            )
            .join("\n")}
    </div>
  );
}
